import type { IsoDate, UserRole } from "../../common/src";
import {
  GUEST_WAIVER_PAGE,
  INVITE_PAGE,
  RESET_PASSWORD_PAGE,
  addDays,
  guestWaiverLinkPath,
  inviteLinkPath,
  isoDateSchema,
  resetPasswordLinkPath,
} from "../../common/src";

const FORGOT_PASSWORD_PAGE = "forgot-password";

/** Trip details carried between the search page and a boat's calendar. */
export interface TripParams {
  startDate?: IsoDate;
  endDate?: IsoDate;
  partySize?: number;
  /** Set when the searcher deliberately cleared the dates to browse every boat. */
  browse?: boolean;
}

export const DEFAULT_PARTY_SIZE = 2;
export const DEFAULT_DAYS_AHEAD = 2;

/**
 * A fresh visit to the search page (no search in the URL) starts with a one-day trip
 * for two people, two days from now. Any explicit search is left as-is.
 */
export const applySearchDefaults = (
  trip: TripParams,
  today: IsoDate
): TripParams => {
  const isFresh =
    !trip.browse &&
    trip.startDate === undefined &&
    trip.endDate === undefined &&
    trip.partySize === undefined;
  if (!isFresh) {
    return trip;
  }
  const day = addDays(today, DEFAULT_DAYS_AHEAD);
  return { startDate: day, endDate: day, partySize: DEFAULT_PARTY_SIZE };
};

export type Route =
  | { page: "find"; trip: TripParams }
  | { page: "boat"; boatId: number; trip: TripParams }
  | { page: "reservations" }
  | { page: "adminBoats" }
  | { page: "adminBoat"; boatId: number | "new" }
  | { page: "availability"; boatId?: number }
  | { page: "users" }
  // Public pages reached from emailed links or the sign-in screen.
  | { page: "invite"; token: string }
  | { page: "resetPassword"; token: string }
  | { page: "forgotPassword" }
  | { page: "guestWaiver"; token: string }
  | { page: "agreement" }
  // Review and sign the Boat Loan Agreement for a trip, then book it.
  | { page: "reserve"; boatId: number; trip: TripParams };

export const DEFAULT_ROUTE: Route = { page: "find", trip: {} };

const positiveInt = (value: string | null | undefined): number | undefined => {
  const n = Number(value);
  return value && Number.isInteger(n) && n > 0 ? n : undefined;
};

const isoDate = (value: string | null): IsoDate | undefined =>
  value !== null && isoDateSchema.safeParse(value).success ? value : undefined;

const readTrip = (params: URLSearchParams): TripParams => ({
  startDate: isoDate(params.get("start")),
  endDate: isoDate(params.get("end")),
  partySize: positiveInt(params.get("party")),
  browse: params.get("browse") === "1" ? true : undefined,
});

const writeTrip = (trip: TripParams): string => {
  const params = new URLSearchParams();
  if (trip.startDate) {
    params.set("start", trip.startDate);
  }
  if (trip.endDate) {
    params.set("end", trip.endDate);
  }
  if (trip.partySize) {
    params.set("party", String(trip.partySize));
  }
  if (trip.browse) {
    params.set("browse", "1");
  }
  const query = params.toString();
  return query ? `?${query}` : "";
};

/** Parses a location hash such as `#/boats/3?start=2026-07-01` into a route. */
export const parseRoute = (hash: string): Route => {
  const [path = "", query = ""] = hash.replace(/^#\/?/, "").split("?");
  const params = new URLSearchParams(query);
  const segments = path.split("/").filter(Boolean);
  const [first, second, third] = segments;

  if (first === "find" || first === undefined) {
    return { page: "find", trip: readTrip(params) };
  }
  if (first === "boats") {
    const boatId = positiveInt(second);
    return boatId
      ? { page: "boat", boatId, trip: readTrip(params) }
      : DEFAULT_ROUTE;
  }
  if (first === "reservations") {
    return { page: "reservations" };
  }
  if (first === "users") {
    return { page: "users" };
  }
  if (first === INVITE_PAGE) {
    return { page: "invite", token: params.get("token") ?? "" };
  }
  if (first === RESET_PASSWORD_PAGE) {
    return { page: "resetPassword", token: params.get("token") ?? "" };
  }
  if (first === FORGOT_PASSWORD_PAGE) {
    return { page: "forgotPassword" };
  }
  if (first === GUEST_WAIVER_PAGE) {
    return { page: "guestWaiver", token: params.get("token") ?? "" };
  }
  if (first === "agreement") {
    return { page: "agreement" };
  }
  if (first === "reserve") {
    const boatId = positiveInt(params.get("boat"));
    return boatId
      ? { page: "reserve", boatId, trip: readTrip(params) }
      : DEFAULT_ROUTE;
  }
  if (first === "availability") {
    return { page: "availability", boatId: positiveInt(params.get("boat")) };
  }
  if (first === "admin" && second === "boats") {
    if (third === "new") {
      return { page: "adminBoat", boatId: "new" };
    }
    const boatId = positiveInt(third);
    return boatId ? { page: "adminBoat", boatId } : { page: "adminBoats" };
  }
  return DEFAULT_ROUTE;
};

/** The hash (including `#/`) for a route; the inverse of `parseRoute`. */
export const routeToHash = (route: Route): string => {
  switch (route.page) {
    case "find":
      return `#/find${writeTrip(route.trip)}`;
    case "boat":
      return `#/boats/${route.boatId}${writeTrip(route.trip)}`;
    case "reservations":
      return "#/reservations";
    case "users":
      return "#/users";
    case "availability":
      return route.boatId
        ? `#/availability?boat=${route.boatId}`
        : "#/availability";
    case "adminBoats":
      return "#/admin/boats";
    case "adminBoat":
      return `#/admin/boats/${route.boatId}`;
    case "invite":
      return `#${inviteLinkPath(route.token).slice(2)}`;
    case "resetPassword":
      return `#${resetPasswordLinkPath(route.token).slice(2)}`;
    case "forgotPassword":
      return `#/${FORGOT_PASSWORD_PAGE}`;
    case "guestWaiver":
      return `#${guestWaiverLinkPath(route.token).slice(2)}`;
    case "agreement":
      return "#/agreement";
    case "reserve": {
      const query = writeTrip(route.trip);
      return `#/reserve?boat=${route.boatId}${query ? `&${query.slice(1)}` : ""}`;
    }
  }
};

type NavKey = "find" | "reservations" | "adminBoats" | "availability" | "users";

interface NavItem {
  key: NavKey;
  label: string;
  route: Route;
  adminOnly: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { key: "find", label: "Find a boat", route: DEFAULT_ROUTE, adminOnly: false },
  {
    key: "reservations",
    label: "Reservations",
    route: { page: "reservations" },
    adminOnly: false,
  },
  {
    key: "adminBoats",
    label: "Boats",
    route: { page: "adminBoats" },
    adminOnly: true,
  },
  {
    key: "availability",
    label: "Availability",
    route: { page: "availability" },
    adminOnly: true,
  },
  { key: "users", label: "Users", route: { page: "users" }, adminOnly: true },
];

export const navItemsFor = (role: UserRole): NavItem[] =>
  NAV_ITEMS.filter((item) => role === "admin" || !item.adminOnly);

/** Which nav item a route belongs under. */
export const navKeyFor = (route: Route): NavKey | undefined => {
  switch (route.page) {
    case "boat":
    case "reserve":
      return "find";
    case "adminBoat":
      return "adminBoats";
    case "invite":
    case "resetPassword":
    case "forgotPassword":
    case "guestWaiver":
    case "agreement":
      return undefined;
    default:
      return route.page;
  }
};

const ADMIN_PAGES: readonly Route["page"][] = [
  "adminBoats",
  "adminBoat",
  "availability",
  "users",
];

const PUBLIC_PAGES: readonly Route["page"][] = [
  "invite",
  "resetPassword",
  "forgotPassword",
  "guestWaiver",
  "agreement",
];

/** Pages anyone can open, signed in or not. */
export const isPublicRoute = (route: Route): boolean =>
  PUBLIC_PAGES.includes(route.page);

export const canView = (role: UserRole, route: Route): boolean =>
  role === "admin" || !ADMIN_PAGES.includes(route.page);
